<!-- Getting it out, as a file. THIS FILE IS YOURS.

     Three things, in the order somebody wants them:

       a file        the PDF, the cabinet list, the drawing set, the model
       a brief       the kitchen in words, for whichever picture tool they use
       the package   all four documents in one share, which is the handover

     The package is what used to be a "Send it to <maker>" button in front of a form. There
     is no maker's inbox and there is not going to be one in this app, so a form that asked
     for a name and an email and then wrote to this device was a promise nobody could keep.
     Sharing the set is the same act told truthfully: it goes wherever they send it.

     Sharing a link and standing the kitchen in your room are NOT here. They act on the
     kitchen, so they are on the kitchen itself, top right (see PlannerPage's `.rail`).

     Each one says what it did, because a share sheet that opened and closed is not a result. -->
<template>
  <div class="exports">
    <p class="label">Download</p>
    <div class="grid">
      <button type="button" @click="pdf" :disabled="busy === 'pdf'">
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 2.5h7l4 4V17.5H5Z"/><path d="M12 2.5v4h4"/><path d="M7.5 11h5"/><path d="M7.5 14h5"/></svg>
        <span>{{ busy === 'pdf' ? 'Making it…' : 'PDF' }}</span>
      </button>
      <button type="button" @click="csv">
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 4h14v12H3Z"/><path d="M3 8h14"/><path d="M3 12h14"/><path d="M8 4v12"/></svg>
        <span>Cabinet list</span>
      </button>
      <button type="button" @click="$emit('drawings')">
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3h14v14H3Z"/><path d="M3 7h14"/><path d="M7 7v10"/><path d="M10 11h4"/></svg>
        <span>Drawings</span>
      </button>
    </div>

    <p class="label">3D model</p>
    <div class="grid">
      <button v-for="f in FORMATS" :key="f.id" type="button" :title="f.note"
              @click="model(f)" :disabled="busy === f.id">
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 2.5 16.5 6v8L10 17.5 3.5 14V6Z"/><path d="M3.5 6 10 9.5 16.5 6"/><path d="M10 9.5v8"/></svg>
        <span>{{ busy === f.id ? 'Building…' : f.name }}</span>
      </button>
    </div>

    <!-- The room as it was on the day it was scanned -- a different file from everything
         above, which is all the DESIGN. Only here at all when this session scanned the
         room: the file lives on the device from the moment RoomPlan wrote it, this is just
         a second door to it (the first is the found-kitchen card, right after scanning). -->
    <template v-if="scanModel">
      <p class="label">As scanned</p>
      <button class="brief" type="button" @click="viewScan">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 2.5 16.5 6v8L10 17.5 3.5 14V6Z"/><path d="M3.5 6 10 9.5 16.5 6"/><path d="M10 9.5v8"/><path d="M10 9.5 3.5 6"/></svg>
        <span>View or share the room as scanned</span>
      </button>
      <p class="fine tight">
        The kitchen exactly as the camera found it, walls and cabinets, before any of this
        design -- so {{ maker.name }} can see the current layout alongside the proposed one.
      </p>
    </template>

    <!-- Not a download, and it stopped pretending to be one. This app draws the kitchen; it
         does not photograph it. What it can do is describe it accurately enough that a
         picture tool draws the right kitchen, and that description is text you paste. -->
    <button class="brief" type="button" @click="brief">
      <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 3h8a2 2 0 0 1 2 2v8"/><path d="M3 7h8a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z"/></svg>
      <span>Copy a render brief</span>
    </button>
    <p class="fine tight">
      This kitchen written out for a picture tool — paste it into whichever one you use, then
      bring the render back under Room. It is words, not a file: nothing here makes photos.
    </p>

    <!-- The whole handover, in one share. -->
    <button class="go" type="button" :disabled="!!busy || disabled" @click="pack">
      {{ busy === 'pack' ? 'Building the package…' : 'Share package' }}
    </button>
    <p class="fine tight">
      Everything a builder or a cabinet maker needs, in one go: the PDF, the drawing set at
      true scale, the cabinet list as a spreadsheet, and the DXF for CAD.
      <template v-if="mustFix"><b>{{ mustFix }} thing{{ mustFix > 1 ? 's' : '' }}</b> a
      designer would fix first — they are listed below, and they go out on the drawings
      whether they are fixed or not.</template>
    </p>

    <p class="fine">
      <b>Drawings</b> is the set a builder works from: a dimensioned plan, an elevation of
      every wall, where the services have to land, and the schedule. As a PDF that prints at
      true scale, or DXF for CAD. The PDF and the cabinet list save to this device; on a
      phone the share sheet offers them to anything you like. <b>Share</b> and
      <b>My room</b> are on the kitchen itself, top right.
    </p>
    <p class="fine">
      The 3D model is the cabinets, not the room, centred and standing on the floor. glTF
      keeps the colours; OBJ and STL are shapes only and come out in millimetres, which is
      what a maker's software expects.
    </p>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { schedule, toCsv } from '../kitchen/schedule.js';
import { planSvg } from '../kitchen/plan-svg.js';
import { buildReport, buildDrawings } from '../kitchen/report.js';
import { sheets } from '../kitchen/drawing.js';
import { toDxf } from '../kitchen/dxf.js';
import { shareFile, shareFiles, shareNativeFile, copyText } from '../kitchen/share.js';
import { renderPrompt } from '../kitchen/prompt.js';
import { FORMATS, exportModel } from '../kitchen/model.js';

const props = defineProps({
  plan: Object, maker: Object, kitchen: Object, estimate: Object, findings: Array,
  mustFix: { type: Number, default: 0 }, disabled: Boolean, scanModel: { type: String, default: null },
});
const emit = defineEmits(['said', 'drawings']);

const busy = ref(null);
const say = (t) => emit('said', t);
const stamp = () => new Date().toISOString().slice(0, 10);
const slug = () => (props.plan.name ? props.plan.name.replace(/[^\w]+/g, '-').toLowerCase() : 'kitchen');

/** The report, built once and used by both the PDF button and the package. */
async function reportBlob() {
  props.kitchen?.select?.(null);
  // A landscape still of the whole kitchen, whatever shape the screen is.
  const shot = props.kitchen?.still?.(1600, 1000) ?? props.kitchen?.snapshot?.(2) ?? null;
  return buildReport({
    plan: props.plan, maker: props.maker, sched: schedule(props.plan, props.maker),
    estimate: props.estimate, shot,
    planSvg: planSvg(props.plan, props.maker, { width: 600, height: 440, pad: 30 }),
    findings: props.findings ?? [],
  });
}

async function pdf() {
  busy.value = 'pdf';
  try {
    const r = await shareFile(await reportBlob(), `${slug()}-${stamp()}.pdf`,
      { title: 'My kitchen', text: `A kitchen from ${props.maker.name}` });
    say({ shared: 'Sent.', downloaded: 'The PDF is in your downloads.', cancelled: null }[r] ?? null);
  } catch (e) {
    say(`Could not make the PDF: ${e?.message ?? e}`);
  } finally { busy.value = null; }
}

async function csv() {
  const blob = new Blob([toCsv(schedule(props.plan, props.maker))], { type: 'text/csv;charset=utf-8' });
  const r = await shareFile(blob, `${props.maker.id}-cabinets-${stamp()}.csv`, { title: 'Cabinet list' });
  say({ shared: 'Sent.', downloaded: 'The list is in your downloads.' }[r] ?? null);
}

async function model(f) {
  busy.value = f.id;
  try {
    props.kitchen?.select?.(null);
    const { blob, filename } = await exportModel(props.kitchen, f.id, { name: props.plan.name });
    const r = await shareFile(blob, filename, { title: 'My kitchen', text: `A kitchen from ${props.maker.name}` });
    say({ shared: 'Sent.', downloaded: `${f.name} saved to your downloads.` }[r] ?? null);
  } catch (e) {
    say(`Could not make the ${f.name}: ${e?.message ?? e}`);
  } finally { busy.value = null; }
}

async function viewScan() {
  if (!props.scanModel) return;
  try {
    const r = await shareNativeFile(props.scanModel,
      { title: 'The kitchen as it is today', text: 'Scanned with LiDAR' });
    if (r === 'shared') say('Sent.');
  } catch (e) {
    say(`Could not open the scan: ${e?.message ?? e}`);
  }
}

async function brief() {
  const text = renderPrompt(props.plan, props.maker, schedule(props.plan, props.maker));
  const r = await copyText(text);
  say(r === 'copied'
    ? 'Copied. Paste it into your picture tool, then bring the render back under Room.'
    : 'Could not copy.');
}

/**
 * The handover: four documents, one share.
 *
 * A3 for the drawings whatever the screen is -- this is the copy that gets printed, and the
 * paper toggle in the preview is for looking, not for sending.
 */
async function pack() {
  busy.value = 'pack';
  try {
    const set = sheets(props.plan, props.maker, { size: 'a3' });
    const [report, drawings] = await Promise.all([reportBlob(), buildDrawings(set)]);
    const files = [
      { blob: report, filename: `${slug()}-${stamp()}.pdf` },
      { blob: drawings, filename: `${slug()}-drawings-${stamp()}.pdf` },
      { blob: new Blob([toCsv(schedule(props.plan, props.maker))], { type: 'text/csv;charset=utf-8' }),
        filename: `${slug()}-cabinets-${stamp()}.csv` },
      { blob: new Blob([toDxf(set)], { type: 'image/vnd.dxf' }),
        filename: `${slug()}-${stamp()}.dxf` },
    ];
    const r = await shareFiles(files, {
      title: 'My kitchen', text: `A kitchen designed with ${props.maker.name}`,
    });
    say({ shared: 'Sent — four documents.',
      downloaded: 'Four documents are in your downloads.', cancelled: null }[r] ?? null);
  } catch (e) {
    say(`Could not build the package: ${e?.message ?? e}`);
  } finally { busy.value = null; }
}
</script>

<style scoped>
.exports { margin-top: 14px; }
.label {
  margin: 0 0 8px; font: 500 11.5px/1 var(--ion-font-family, system-ui);
  letter-spacing: .05em; text-transform: uppercase; color: var(--k-mute);
}
.label + .grid { margin-bottom: 14px; }
.grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(96px, 1fr)); gap: 7px; }
.grid button {
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px;
  min-height: 62px; padding: 8px 6px; border-radius: 11px; cursor: pointer;
  border: 1px solid var(--k-line); background: var(--k-paper); color: var(--k-dim);
  font: 500 12px/1.2 var(--ion-font-family, system-ui);
}
.grid button:active { background: var(--k-fill); }
.grid button:disabled { opacity: .55; }

.brief {
  width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px;
  border: 1px solid var(--k-line); border-radius: 11px; cursor: pointer;
  background: var(--k-paper); color: var(--k-dim);
  font: 500 13px/1 var(--ion-font-family, system-ui);
  padding: 13px; min-height: 46px; margin-top: 2px;
}
.brief:active { background: var(--k-fill); }

.go {
  width: 100%; border: 0; border-radius: 12px; cursor: pointer;
  background: var(--k-ink); color: var(--k-paper);
  font: 600 15px/1 var(--ion-font-family, system-ui);
  padding: 17px; min-height: 54px; margin-top: 16px;
}
.go:disabled { opacity: .5; }

.fine { margin: 10px 0 0; font: 400 11.5px/1.4 var(--ion-font-family, system-ui); color: var(--k-mute); }
.fine.tight { margin-top: 7px; }
</style>
